import React, { Component } from 'react'
import withNotifications from 'components/shared/notifications'
import { without } from 'ramda'
import Select from 'react-select-plus'
import { api } from 'app/modules/sfdc/api'
import ErrorState from './ErrorState'
import { Button } from '@driftt/tide-core-legacy'
import './style.styl'

const DEFAULT_PROPERTIES = ['DOMAIN', 'OWNER', 'NAME']
const EMPTY_MAPPING = { toAttribute:null, fromKey: null, type: 'SALESFORCEACCOUNT' }
const DRIFT_DEFAULT_PROPERTY_LABELS = {
  "DOMAIN": "Domain",
  "OWNER": "Account Owner",
  "NAME": "Name"
}
const DEFAULT_PROPERTY_DEFINITIONS = [
  {
    "label" : "Domain",
    "name" : "DOMAIN",
    "type" : "URL"
  },
  {
    "label" : "Name",
    "name" : "NAME",
    "type" : "STRING"
  },
  {
    "label" : "Account Owner",
    "name" : "OWNER",
    "type" : "TEAMMEMBER"
  }
]
const SFDC_ACCOUNT_PROPERTY_TYPE_MAPPING = {
  "STRING": ["base64", "byte", "string", "time", "address", "anyType", "combobox", "encryptedstring", "ID", "reference", "textarea", "boolean"],
  "NUMBER": ["double", "int", "currency", "percent"],
  "ENUM": [ "picklist"],
  "DATE": "date",
  "DATETIME": "dateTime",
  "EMAIL": "email",
  "PHONE": "phone",
  "ENUMARRAY": "multipicklist",
  "URL": "url",
  "TEAMMEMBER": ["ID", "email", "reference"]
}

class AccountPropertyMapping extends Component {

  constructor(props) {
    super(props)

    this.state = {
      driftAccountProperties: [],
      SFDCAccountProperties: [],

      usedDriftProperties: [],
      usedSFDCProperties: [],

      error: null,
      loadingPreviews: false,
      editingSFDCProperty: null,
      staticValues: [{ label: 'N/A', value: 'N/A' }],
      mappings: [],
      initialMappings: [],
      previews: [],
      toDelete: []
    }
  }

  componentDidMount() {
    this.fetchMappings()
    this.fetchDriftProperties()
    this.fetchSFDCProperties()
  }

  fetchSFDCProperties = () => {
    api.fetchSFDCAccountProperties()
      .then(data => {
        this.setState({
          SFDCAccountProperties: data
        })
      })
      .catch(err => {
        this.setState({
          error: err
        })
      })
  }

  fetchDriftProperties = () => {
    api.fetchDriftAccountProperties()
      .then(data => {
        data = data.concat(DEFAULT_PROPERTY_DEFINITIONS)
        this.setState({
          driftAccountProperties: data
        })
      })
      .catch(err => {
        this.setState({
          error: err
        })
      })
  }

  fetchMappings = () => {
    api.fetchSFDCAccountPropertyMappings()
    .then(data => {
      this.setState({
        mappings: data,
        initialMappings: data
      })
    })
    .catch(err => {
      this.setState({
        error: err
      })
    })
  }

  setSuccess = () => {
    this.setState(prevState => ({
      initialMappings: prevState.mappings
    }), this.forceUpdate)
  }

  isClean = () => {
    return _.isEqual(this.state.mappings, this.state.initialMappings)
  }

  isValid = () => {
    if (!this.state.mappings.length) return false
    let isValid = true

    this.state.mappings.forEach(mapping => {
      if ((!mapping.toAttribute) || !mapping.fromKey) {
        isValid = false
      }
    })

    return isValid
  }

  deleteMappings = () => {
    const updatedMappings = [...this.state.mappings]
    const mappingsToDelete = this.state.toDelete
      .map(index => {
        const id = updatedMappings[index] || null
        updatedMappings.splice(index, 1)        
        if (!!id) {
          return id
        }
      })

    this.setState({
      mappings: updatedMappings,
      toDelete: []
    }, () => this.saveDeleteMappings(mappingsToDelete))
  }

  patchMappings = () => {
    const successCB = () => this.props.notifications.success('Salesforce mapping updated!')
    const errorCB = () => this.props.notifications.error('We encountered an error saving your mappings.')
    api.patchSFDCAccountPropertyMappings(this.state.mappings)
      .then(this.fetchMappings)
      .then(successCB)
      .catch(errorCB)
  }

  saveDeleteMappings = mappingsToDelete => {
    const successCB = () => this.props.notifications.success('Salesforce mapping updated!')
    const errorCB = () => this.props.notifications.error('We encountered an error deleting your mappings.')
    api.deleteSFDCAccountPropertyMappings(mappingsToDelete)
      .then(this.setSuccess)
      .then(successCB)
      .catch(errorCB)
  }

  addNewMapping = () => {
    const updatedMappings = [...this.state.mappings]
    updatedMappings.push(Object.assign({}, EMPTY_MAPPING))

    this.setState({ mappings: updatedMappings })
  }

  updateDeleteProperties = ({ target }) => {
    this.setState(prevState => {
      const { value, checked } = target
      const updatedToDelete = checked
        ? [...prevState.toDelete, value]
        : without(value, prevState.toDelete)

      return {
        toDelete: updatedToDelete
      }
    })
  }

  updateDriftPropertyInMapping(value, mappingIndex) {
    const updatedMappings = _.cloneDeep(this.state.mappings)
    updatedMappings[mappingIndex].toAttribute = value.value

    this.setState({
      mappings: updatedMappings 
    })
  }

  updateSFDCPropertyInMapping(value, mappingIndex) {
    const updatedMappings = _.cloneDeep(this.state.mappings)
    updatedMappings[mappingIndex].fromKey = value.value

    this.setState({ mappings: updatedMappings })
  }

  renderDriftPropertiesSelect(driftProperty, mappingIndex) {
    const combinedOptions = [...this.state.driftAccountProperties].map(({ label, name }) => ({ label: label, value: name }))
    const selectedValue = driftProperty ? combinedOptions.filter(property => property.value == driftProperty)[0] : null

    const selectOptions = {
      multi: false,
      searchable: true,
      autoFocus: false,
      clearable: false,
      value: selectedValue,
      options: combinedOptions,
      className: "salesforce-mapping-drift-attribute-select",
      onChange: (value) => this.updateDriftPropertyInMapping(value, mappingIndex)
    }

    return (<Select {...selectOptions} />)
  }

  renderSFDCSelect(mappingIndex, fromKey) {
    const supportedProperties = this.state.SFDCAccountProperties
      .filter(property => {
        const driftProperty = this.state.mappings[mappingIndex].toAttribute
        const driftPropertyDefinition = [...this.state.driftAccountProperties]
          .filter(accountProperty => accountProperty.name === driftProperty)[0] || {}
          

        const driftType = driftPropertyDefinition.type || null
        const isSupportedType = driftType && SFDC_ACCOUNT_PROPERTY_TYPE_MAPPING[driftType].includes(property.type)

        return (isSupportedType || !driftType)
      })
    const formattedOptions = supportedProperties.map(property => { return { label: property.label, value: property.name }})
    const selectedValue = fromKey ? formattedOptions.filter(property => property.value == fromKey)[0] : null

    const selectOptions = {
      multi: false,
      searchable: true,
      autoFocus: false,
      clearable: false,
      value: selectedValue,
      options: formattedOptions,
      onChange: (value) => this.updateSFDCPropertyInMapping(value, mappingIndex)
    }

    return (<Select {...selectOptions} />)
  }

  renderRequiredTableRows() {
    return this.state.mappings.map((mapping, index) => {
      const { fromKey, toAttribute } = mapping
      const setIsEditing = () => this.setState({ editingSFDCProperty: index })
      const isUneditable = DEFAULT_PROPERTIES.includes(toAttribute)
      const salesforceProperty = this.renderSFDCSelect(index, fromKey)
    
      return (
        <tr key={index}>
          <td><input disabled={(isUneditable)} value={index} type="checkbox" onChange={this.updateDeleteProperties} /></td>
          <td className="salesforce-mapping-drift-attribute">
            {isUneditable ?
              <input value={DRIFT_DEFAULT_PROPERTY_LABELS[toAttribute]} type="text" disabled /> :
              this.renderDriftPropertiesSelect(toAttribute, index)
            }
            </td>
          <td onClick={setIsEditing}>{ salesforceProperty }</td>
        </tr>
      )
    })
  }

  render() {
    if (this.state.error) {
      return <ErrorState />
    }

    return (
        <div>
          <div className="salesforce-mapping-actions">
            <p className="salesforce-mapping-account-custom">Head over to
              <a href="/accounts"> your Accounts page </a>
              to create custom properties pulled from Salesforce.
            </p>
            <span><strong>*</strong>These are your required Salesforce Fields</span>
            <Button 
              disabled={!this.state.toDelete.length} 
              onClick={this.deleteMappings}>
              Delete
            </Button>
            <Button 
              disabled={this.isClean() || !this.isValid()} 
              onClick={this.patchMappings}>
              Update
            </Button>
          </div>

          <table className="drift-table salesforce-mapping-table drift-form">
            <thead>
              <tr>
                <th></th>
                <th className="salesforce-mapping-drift-attribute">Drift Attribute or Value</th>
                <th>Salesforce Field</th>
              </tr>
            </thead>
            <tbody>
              {this.renderRequiredTableRows()}
            </tbody>
          </table>
          <button className="drift-button salesforce-mapping-add-field" onClick={this.addNewMapping}>Add New Field</button>
      </div>
    )
  }
}

export default withNotifications(AccountPropertyMapping)



// WEBPACK FOOTER //
// ./src/scripts/components/Integration/pages/SalesforceMapping/AccountPropertyMapping.js